import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { PlusIcon, SearchIcon, CaretIcon, RefreshIcon, EditLineIcon, TrashIcon } from '../../assets/icons';
import {
  useGetReferenceDirectoryQuery,
  useGetReferenceDirectoryFiltersQuery,
  refName,
  refPhone,
  type ReferencePersonRecord,
} from '../../features/referenceDirectory/referenceDirectoryApi';
import { useDeleteUserMutation } from '../../features/users/usersApi';
import { useActiveOrgId } from '../../features/organizations/useActiveOrg';

/* ------------------------------------------------------------------ *
 * Important Person directory — listing (Directory → Important Person).
 * Backed by GET /reference-directory/ (server-side search, department
 * filter, pagination). Same layout as the Government Official list.
 * ------------------------------------------------------------------ */

const AVATARS = [
  'bg-[#e0edff] text-[#2563eb]',
  'bg-[#ffe4e6] text-[#e11d48]',
  'bg-[#dcfce7] text-[#15803d]',
  'bg-[#ccfbf1] text-[#0f766e]',
  'bg-[#fef3c7] text-[#b45309]',
];

const initialsOf = (name: string) => {
  const words = name.split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  return (words.slice(0, 2).map((w) => w[0]).join('') || name.slice(0, 2)).toUpperCase();
};

const GRID =
  'grid grid-cols-[minmax(220px,1.6fr)_minmax(150px,1fr)_minmax(150px,1fr)_150px_100px] items-center';

function Row({
  person,
  avatar,
  onEdit,
  onDelete,
}: {
  person: ReferencePersonRecord;
  avatar: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={[GRID, 'border-t border-[#f3f4f6]'].join(' ')}>
      {/* Name */}
      <div className="flex items-center gap-3 px-6 py-4">
        <span
          className={[
            'flex size-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold',
            avatar,
          ].join(' ')}
        >
          {initialsOf(refName(person))}
        </span>
        <p className="truncate text-[13px] font-semibold leading-5 text-[#111827]">{refName(person)}</p>
      </div>

      {/* Department */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{person.department || '—'}</div>

      {/* Designation */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{person.designation || '—'}</div>

      {/* Contact */}
      <div className="px-6 py-4 text-xs text-[#475569]">{refPhone(person) || '—'}</div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-6 py-4">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${refName(person)}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <EditLineIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${refName(person)}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function ImportantPersonPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState<string>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Organization comes from the header-wide picker.
  const organization = useActiveOrgId();

  useEffect(() => setPage(1), [search, department, pageSize, organization]);

  const { data: filters } = useGetReferenceDirectoryFiltersQuery();
  const { data, isFetching, isError, refetch } = useGetReferenceDirectoryQuery({
    search: search.trim() || undefined,
    department,
    organization: organization ?? undefined,
    page,
    pageSize,
  });
  // Records are users (directory=non_elected); edit/delete target the users resource.
  const [deleteUser, { isLoading: deleting }] = useDeleteUserMutation();
  const [toDelete, setToDelete] = useState<ReferencePersonRecord | null>(null);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const departmentOpts = filters?.departments ?? [];

  const resetFilters = () => {
    setSearch('');
    setDepartment(undefined);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteUser(toDelete.id).unwrap();
      message.success('Important person deleted.');
      setToDelete(null);
      refetch();
    } catch {
      message.error('Could not delete the record.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header + CTA */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold leading-8 text-[#111827]">Important Persons</h1>
          <p className="text-xs leading-4 text-[#6b7280]">
            Manage influential people across departments in your constituency.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/users/others/new')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-4" />
          Create Important Person
        </button>
      </div>

      {/* Filter & search toolbar */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[280px] flex-1">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name..."
              className="w-full rounded-lg border border-[#e5e7eb] bg-white py-2.5 pl-10 pr-4 text-xs text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <Select
            className="min-w-[200px]"
            allowClear
            placeholder="Department"
            value={department}
            onChange={setDepartment}
            options={departmentOpts.map((o) => ({ value: o, label: o }))}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Reset filters"
            className="rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[820px]">
            {/* Header */}
            <div className={[GRID, 'bg-[#f9fafb]/50 border-b border-[#f3f4f6]'].join(' ')}>
              {(
                [
                  ['NAME', 'justify-start'],
                  ['DEPARTMENT', 'justify-start'],
                  ['DESIGNATION', 'justify-start'],
                  ['CONTACT', 'justify-start'],
                  ['ACTIONS', 'justify-end text-right'],
                ] as const
              ).map(([label, align]) => (
                <div key={label} className={['flex px-6 py-[14px]', align].join(' ')}>
                  <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#6b7280]">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Body */}
            {isError ? (
              <p className="px-6 py-12 text-center text-sm text-red-500">
                Could not load important persons. Please try again.
              </p>
            ) : rows.length > 0 ? (
              rows.map((person, i) => (
                <Row
                  key={person.id}
                  person={person}
                  avatar={AVATARS[i % AVATARS.length]}
                  onEdit={() => navigate(`/admin/users/others/${person.id}/edit`)}
                  onDelete={() => setToDelete(person)}
                />
              ))
            ) : (
              <p className="px-6 py-12 text-center text-sm text-ink-secondary">
                {isFetching ? 'Loading…' : 'No important persons match your filters.'}
              </p>
            )}
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="important persons"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={toDelete !== null}
        title="Delete Important Person"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">{toDelete ? refName(toDelete) : ''}</span>? This
            action cannot be undone.
          </>
        }
        loading={deleting}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
